import * as React from 'react';

import styled, { css, useTheme } from 'styled-components';

import { motion } from 'framer-motion';

import { easeTransition } from '@components/framer';

export interface CircleRightArrowIconProps {
  className?: string;
  disabled?: boolean;
  onClick?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  isDark?: boolean;
  isWhite?: boolean;
  isRed?: boolean;
  noHover?: boolean;
  isHovered?: boolean;
  size?: number;
  darkerCircleColor?: boolean;
}

export const CircleRightArrowIcon: React.FC<CircleRightArrowIconProps> = ({
  className,
  disabled,
  onClick,
  isDark,
  isWhite,
  isRed,
  noHover,
  isHovered,
  size,
  darkerCircleColor,
}) => {
  const theme = useTheme();

  const iconMotion = {
    rest: { x: 0, scale: 1 },
    hover: {
      x: -2,
      scale: 1.0625,
      transition: {
        ...easeTransition,
      },
    },
    tap: { x: 100, scale: 0.95 },
  };

  const getCircleColor = () => {
    if (isWhite) {
      return theme.colors.white;
    } else if (isRed) {
      return theme.colors.accent;
    } else if (isDark) {
      return theme.colors.primary;
    } else if (darkerCircleColor) {
      return theme.colors.circleBgIcon;
    }

    return theme.colors.header;
  };

  const getPathColor = () => {
    if (isWhite) {
      return theme.colors.black;
    } else if (isRed) {
      return theme.colors.white;
    } else if (isDark) {
      return theme.colors.white;
    } else if (darkerCircleColor) {
      return theme.colors.primary;
    } else if (disabled) {
      return theme.colors.secondary;
    }

    return theme.colors.primary;
  };

  return (
    <motion.svg
      width={size ? size : '30'}
      height={size ? size : '30'}
      viewBox="0 0 30 30"
      className={className}
      initial="rest"
      whileHover={disabled || noHover ? 'rest' : 'hover'}
      whileTap={disabled || noHover ? 'rest' : 'tap'}
      animate={isHovered ? 'hover' : 'rest'}
      onClick={(ev) => (onClick ? onClick(ev) : null)}
    >
      <circle cx="15" cy="15" r="15" fill={getCircleColor()} />
      <motion.path
        stroke={getPathColor()}
        variants={iconMotion}
        d="M18.9 14.6l-3-3c-0.2-0.2-0.5-0.2-0.7 0s-0.2 0.5 0 0.7l2.1 2.1h-5.8c-0.3 0-0.5 0.2-0.5 0.5s0.2 0.5 0.5 0.5h5.8l-2.1 2.1c-0.2 0.2-0.2 0.5 0 0.7 0.1 0.1 0.2 0.1 0.4 0.1s0.3 0 0.4-0.1l3-3C19 15.2 19 14.8 18.9 14.6z"
      />
    </motion.svg>
  );
};

export default styled(CircleRightArrowIcon)`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  width: ${(props) => (props.size ? `${props.size}px` : '30px')};
  height: ${(props) => (props.size ? `${props.size}px` : '30px')};

  path {
    transition: fill
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  ${(props) =>
    props.disabled
      ? null
      : props.noHover
      ? null
      : css`
          circle {
            transition: fill
              ${({ theme }) =>
                `${theme.transitions.duration} ${theme.transitions.type}`};
          }
          &:hover {
            fill: ${({ theme }) => theme.colors.secondaryDark};
          }
        `}
  ${(props) =>
    props.isRed
      ? css`
          circle {
            transition: fill
              ${({ theme }) =>
                `${theme.transitions.duration} ${theme.transitions.type}`};

            &:hover {
              fill: ${({ theme }) => theme.colors.accentHover};
            }
          }
        `
      : null}
`;
